import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import Switch from '@/design-system/atoms/Forms/Switch';
import { IconList } from '@/design-system/atoms/Icons/IconList';
import { IconMap } from '@/design-system/atoms/Icons/IconMap';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useLocationSearchContext } from '../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { useLocationViewState } from '../../hooks/useLocationViewState';
import styles from './LocationToolbar.module.scss';

/**
 * The Location Toolbar is a component that displays the total number of results, the search radius and allows the user to toggle between map and list views.
 */
const LocationToolbar = ({ cta }) => {
    const searchParams = useSearchParams();

    const { openNowLabel, openNowAriaLabel, largeBreakpoint } = useLocationUIContext();

    const {
        coveo: {
            controller: { querySummary, resultList },
        },
        handleShowResultList,
        handleShowMaps,
        handleOpenNow,
    } = useLocationSearchContext();

    const viewState = useLocationViewState();

    const resultsText = useRef();

    const breakpoints = useBreakpoint();
    const [totalResults, setTotalResults] = useState(null);
    const [distance, setDistance] = useState('');
    const [searchResponseId, setSearchResponseId] = useState(null);

    const openNow = searchParams.get('open-now') === 'true';

    /**
     * Scroll to and focus on the results text element
     */
    const scrollAndFocus = useCallback(() => {
        if (resultsText?.current && !breakpoints?.large) {
            resultsText.current.setAttribute('tabIndex', '0');
            resultsText.current.focus();
            resultsText.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [resultsText, breakpoints]);

    useEffect(() => {
        return querySummary.subscribe(() => {
            const { total } = querySummary.state;

            setTotalResults(total);
        });
    }, [querySummary]);

    /**
     * Subscribe to changes in the resultList to manage focus on resultsText when new results are loaded
     * and to update the searchResponseId to detect new searches.
     */
    useEffect(() => {
        return resultList.subscribe(() => {
            const { searchResponseId: newSearchResponseId } = resultList.state;

            if (newSearchResponseId && newSearchResponseId !== searchResponseId) {
                setSearchResponseId(newSearchResponseId);
                scrollAndFocus();
            }
        });
    }, [resultList, searchResponseId, scrollAndFocus]);

    useEffect(() => {
        const searchParamDistance = searchParams.get('distance');

        if (!searchParamDistance) {
            setDistance('');
            return;
        }

        setDistance(searchParamDistance);
    }, [searchParams]);

    return (
        <section className={styles[`location-toolbar`]}>
            <div
                className="type-t1"
                ref={resultsText}
                onBlur={() => resultsText.current?.removeAttribute('tabIndex')}
            >
                {totalResults != null && (
                    <>
                        {totalResults} results
                        {distance && (
                            <>
                                {' '}
                                <span className="type-t1b">within {distance} miles</span>
                            </>
                        )}
                    </>
                )}
            </div>
            <div className={styles[`location-toolbar__toggles`]}>
                {breakpoints?.large && (
                    <>
                        <Button
                            label={
                                viewState === 'result-list'
                                    ? largeBreakpoint?.mapView?.label
                                    : largeBreakpoint?.listView?.label
                            }
                            ariaLabel={
                                viewState === 'result-list'
                                    ? largeBreakpoint?.mapView?.ariaLabel
                                    : largeBreakpoint?.listView?.ariaLabel
                            }
                            buttonStyle={Button.STYLE.TERTIARY}
                            iconComponent={viewState === 'result-list' ? IconMap : IconList}
                            onClick={() => {
                                viewState === 'result-list' && handleShowMaps();
                                viewState === 'maps' && handleShowResultList();
                            }}
                        />
                        <div className={styles[`location-toolbar__divider`]} />
                    </>
                )}
                <Switch
                    isSelected={openNow}
                    onChange={handleOpenNow}
                    label={openNowLabel}
                    aria-label={openNowAriaLabel}
                />
            </div>
        </section>
    );
};

LocationToolbar.propTypes = {
    /**
     * The search radius
     */
    radius: PropTypes.number,
};

export default LocationToolbar;
